
import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import PageLayout from '@/components/layout/PageLayout';
import { getBlogPostBySlug } from '@/services/flotiqApi';
import { formatPublicationDate, renderRichTextContent, generateArticleSchema } from '@/utils/blogUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blogPost', slug],
    queryFn: () => getBlogPostBySlug(slug || ''),
    enabled: !!slug
  });

  // Direct DOM manipulation to update meta tags after the post loads
  // Enhanced for better OTTO SEO tracking
  useEffect(() => {
    if (post) {
      // Set title directly
      document.title = `${post.title} | Astrem Blog`;
      
      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', post.excerpt);
      } else {
        const newMetaDesc = document.createElement('meta');
        newMetaDesc.setAttribute('name', 'description');
        newMetaDesc.setAttribute('content', post.excerpt);
        document.head.appendChild(newMetaDesc);
      }
      
      // Set Open Graph meta tags
      const updateMetaTag = (selector: string, content: string) => {
        const tag = document.querySelector(selector);
        if (tag) {
          tag.setAttribute('content', content);
        } else {
          // Create the tag if it doesn't exist - for better OTTO detection
          const newTag = document.createElement('meta');
          if (selector.includes('property=')) {
            newTag.setAttribute('property', selector.split('property="')[1].split('"')[0]);
          } else if (selector.includes('name=')) {
            newTag.setAttribute('name', selector.split('name="')[1].split('"')[0]);
          }
          newTag.setAttribute('content', content);
          document.head.appendChild(newTag);
        }
      };
      
      const currentUrl = `https://astrem.co/blog/${post.slug}`;
      
      updateMetaTag('meta[property="og:title"]', post.title);
      updateMetaTag('meta[property="og:description"]', post.excerpt);
      updateMetaTag('meta[property="og:url"]', currentUrl);
      updateMetaTag('meta[property="og:type"]', 'article');
      
      if (post.thumbnail && post.thumbnail[0] && post.thumbnail[0].url) {
        updateMetaTag('meta[property="og:image"]', post.thumbnail[0].url);
        updateMetaTag('meta[name="twitter:image"]', post.thumbnail[0].url);
      }
      
      updateMetaTag('meta[name="twitter:title"]', post.title);
      updateMetaTag('meta[name="twitter:description"]', post.excerpt);
      
      // Set canonical link
      const canonicalTag = document.querySelector('link[rel="canonical"]');
      if (canonicalTag) {
        canonicalTag.setAttribute('href', currentUrl);
      } else {
        const newCanonicalTag = document.createElement('link');
        newCanonicalTag.setAttribute('rel', 'canonical');
        newCanonicalTag.setAttribute('href', currentUrl);
        document.head.appendChild(newCanonicalTag);
      }
      
      // Trigger an event to notify OTTO of content change
      const ottoEvent = new CustomEvent('ottoContentUpdated', {
        detail: { 
          path: window.location.pathname,
          title: post.title,
          description: post.excerpt,
          type: 'article'
        }
      });
      document.dispatchEvent(ottoEvent);
    }
  }, [post]);

  if (isLoading) {
    return (
      <PageLayout title="Loading Blog Post" description="Loading blog post content" navStyle="default">
        <div className="bg-astrem-black text-white pt-36 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Skeleton className="w-full h-10 mb-4 bg-gray-700" />
              <Skeleton className="w-1/3 h-6 mb-8 bg-gray-700" />
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto">
            <Skeleton className="w-full h-6 mb-4" />
            <Skeleton className="w-full h-6 mb-4" />
            <Skeleton className="w-3/4 h-6 mb-4" />
            <Skeleton className="w-full h-6 mb-4" />
            <Skeleton className="w-5/6 h-6 mb-4" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !post) {
    return (
      <PageLayout title="Post Not Found" description="The requested blog post could not be found" navStyle="default">
        <div className="bg-astrem-black text-white pt-36 pb-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold mb-4">Blog Post Not Found</h1>
            <p className="mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
          </div>
        </div>
        <div className="container mx-auto px-4 py-16 text-center">
          <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800">
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  const publicationDate = post.publicationDate || post.publicationdate;
  const renderedContent = renderRichTextContent(post.content);
  const currentUrl = `https://astrem.co/blog/${post.slug}`;
  
  // Generate schema as an object, then ensure it's in an array
  const articleSchemaObject = generateArticleSchema(post);
  const schemaDataArray = [articleSchemaObject];

  return (
    <PageLayout 
      title={`${post.title} | Astrem Blog`}
      description={post.excerpt}
      navStyle="default"
      canonicalPath={`/blog/${post.slug}`}
      schemaData={schemaDataArray}
    >
      <Helmet>
        <title>{post.title} | Astrem Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <link rel="canonical" href={currentUrl} />
        {post.thumbnail && post.thumbnail[0] && post.thumbnail[0].url && (
          <meta property="og:image" content={post.thumbnail[0].url} />
        )}
      </Helmet>

      {/* Black background hero section for the blog post title */}
      <div className="bg-astrem-black text-white pt-36 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Link 
              to="/blog"
              className="inline-flex items-center text-gray-300 hover:text-white transition-colors mb-8"
              aria-label="Back to Blog"
            >
              <div className="flex items-center cursor-pointer p-2">
                <ArrowLeft size={16} className="mr-2" aria-hidden="true" />
                <span>Back to Blog</span>
              </div>
            </Link>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>
            <div className="flex items-center text-gray-300">
              <Calendar size={16} className="mr-2" aria-hidden="true" />
              <time dateTime={publicationDate || ''}>
                {publicationDate ? formatPublicationDate(publicationDate) : 'No date available'}
              </time>
            </div>
          </div>
        </div>
      </div>

      <article className="bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            {/* Enhanced blog post content styling */}
            <div 
              className="prose prose-lg max-w-none 
                prose-headings:font-display prose-headings:font-medium prose-headings:mb-4 prose-headings:mt-8
                prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl 
                prose-p:mb-4 prose-p:leading-relaxed
                prose-a:text-blue-600 prose-a:font-medium prose-a:underline hover:prose-a:text-blue-800
                prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic
                prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
                prose-li:mb-2
                prose-img:my-8 prose-img:rounded-lg
                [&_table]:w-full [&_table]:border-collapse [&_table]:my-6
                [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-100 [&_th]:p-2 [&_th]:font-semibold
                [&_td]:border [&_td]:border-gray-300 [&_td]:p-2 
                [&_tr:nth-child(even)]:bg-gray-50"
              dangerouslySetInnerHTML={{ __html: renderedContent }}
            />
            
            {/* Internal links section */}
            {post.internalLinks && post.internalLinks.length > 0 && (
              <div className="mt-16 pt-8 border-t">
                <h2 className="text-2xl font-semibold mb-6">Related Articles</h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {post.internalLinks.map((link, index) => (
                    <Link 
                      key={index}
                      to={link.dataUrl}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      aria-label={`Read related article: ${link.title}`}
                    >
                      <span className="font-medium">{link.title}</span>
                      <ArrowRight size={16} aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </PageLayout>
  );
};

export default BlogPost;
